// One-shot attempt-marking run detail — the marking of one completed attempt
// against its whole mark-scheme document. Reads the DEDICATED
// `mark-attempt-runs` record (the OperationRun), then its model runs (keyed by
// the session id, which the marking ModelOutputs are saved against).
//
// The left card is a marking-specific cross-check panel rather than the generic
// context block. Every other operation's failure is visible as an error; this
// one's is a green run with wrong numbers, so the counters that would expose
// that — Σ tariffs vs the scheme's stated maximum, questions the model returned
// nothing for, credits routed to a human — are given first-class space.
//
// Mirrors QuestionSessionReportDetail using the shared operation-detail components.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
    formatCost,
    totalRunCost,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

export function MarkAttemptDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const { data: runData, isLoading, error } = useQuery({
        queryKey: ['markAttemptRun', id],
        queryFn: () => adminApi.getMarkAttemptRun(id!),
        enabled: !!id,
    });
    const run = runData?.run;

    // Marking ModelOutputs are saved against the session id (submissionId).
    const sessionId = run?.correlation?.sessionId;
    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', sessionId],
        queryFn: () => adminApi.getModelRuns(sessionId!),
        enabled: !!sessionId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find((r) => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=markattempt');

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading marking run…</div></div>;
    }
    if (error || !run) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading marking run: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Attempt Marking</button>
            </div>
        );
    }

    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Attempt Marking Detail' },
    });
    const selectedRun = runs.find((r) => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRawResponse));

    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRawResponse}
        />
    ) : undefined;

    // The document-supplied invariant. Absent when the scheme printed no cover
    // total — which is itself worth saying, since "no check ran" and "the check
    // passed" look identical if you only show a number.
    const o = run.outcome;
    const crossCheck = !o
        ? null
        : o.statedMaximumMark == null
            ? { label: `Σ ${o.tariffSum} · scheme states no maximum`, ok: undefined }
            : {
                label: `Σ ${o.tariffSum} vs ${o.statedMaximumMark} stated`,
                ok: o.totalsAgree === true,
            };

    return (
        <OperationDetailPage
            title="Attempt Marking Details"
            id={run.id}
            backLabel="Attempt Marking"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Attempt" />}
            onBack={goBack}
            context={
                <ContextGrid
                    left={
                        <MetadataCard title="Marking">
                            <MetaItem label="Student">
                                {run.studentName || run.userId}
                                {run.studentEmail && <span className="muted"> · {run.studentEmail}</span>}
                            </MetaItem>
                            <MetaItem label="Session">
                                <span className="datetime" style={{ fontFamily: 'monospace' }}>{sessionId || '—'}</span>
                            </MetaItem>
                            {run.correlation?.questionSetId && (
                                <MetaItem label="Assignment">
                                    <a role="button" style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/question-sets/${run.correlation!.questionSetId}`)}>
                                        {run.correlation.questionSetId} →
                                    </a>
                                </MetaItem>
                            )}
                            {o && (
                                <MetaItem label="Marks awarded">
                                    <strong>{o.marksAwarded}</strong> / {o.maxMarks}
                                </MetaItem>
                            )}
                            {crossCheck && (
                                <MetaItem label="Tariff cross-check">
                                    <span className={`tag ${crossCheck.ok === false ? 'status-failed' : ''}`}>
                                        {crossCheck.label}
                                    </span>
                                    {crossCheck.ok === false && (
                                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                            The scheme was misread somewhere — these marks are suspect.
                                        </div>
                                    )}
                                </MetaItem>
                            )}
                            {o && (
                                <MetaItem label="Questions">
                                    {o.questionsMarked} marked of {o.questionsReturned} returned
                                    <span className="muted"> · {o.questionsWithCredits} with credit items</span>
                                </MetaItem>
                            )}
                            {!!o?.unreturnedIndices?.length && (
                                <MetaItem label="Not returned">
                                    <span className="tag status-failed">Q{o.unreturnedIndices.join(', Q')}</span>
                                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                        Attempted, but the model returned no entry — these kept their fallback marks.
                                    </div>
                                </MetaItem>
                            )}
                            {!!o?.unmatchedIndices?.length && (
                                <MetaItem label="Unmatched">
                                    <span className="tag status-failed">index {o.unmatchedIndices.join(', ')}</span>
                                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                        Returned entries matching no question on this attempt.
                                    </div>
                                </MetaItem>
                            )}
                            {!!o?.manualCredits && (
                                <MetaItem label="Needs a human">
                                    {o.manualCredits} credit item{o.manualCredits === 1 ? '' : 's'} matched but not
                                    machine-awardable (diagram / construction).
                                </MetaItem>
                            )}
                            {run.schemeBlobPath && (
                                <MetaItem label="Mark scheme">
                                    <span className="datetime" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                        {run.schemeBlobPath}
                                    </span>
                                    <span className="muted">
                                        {' '}· {run.schemeSourceKind ?? 'pdf'}
                                        {o?.schemePages ? ` · ${o.schemePages} pages sent` : ''}
                                    </span>
                                </MetaItem>
                            )}
                        </MetadataCard>
                    }
                    right={
                        <MetadataCard title="Run">
                            <MetaItem label="Status"><span className={`tag status-${run.status}`}>{run.status}</span></MetaItem>
                            <MetaItem label="Latency">{formatLatency(run.latencyMs)}</MetaItem>
                            <MetaItem label="Started">
                                <span className="datetime">{format(new Date(run.startedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            {run.completedAt && (
                                <MetaItem label="Completed">
                                    <span className="datetime">{format(new Date(run.completedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            )}
                            {run.llm && (
                                <MetaItem label="Tokens / Cost">
                                    {run.llm.inputTokens.toLocaleString()} in / {run.llm.outputTokens.toLocaleString()} out
                                    {/* Every call of the operation (the runs below), not the record's own figure. */}
                                    {(totalRunCost(runs) ?? run.llm.costUsd) != null && <span className="muted"> · {formatCost(totalRunCost(runs) ?? run.llm.costUsd!)}</span>}
                                </MetaItem>
                            )}
                            {run.error && (
                                <MetaItem label="Error"><span className="tag status-failed">{run.error}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
            }
            runs={runs.length > 0 ? (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={(r) => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={(r) => setSelectedRunId(r.id)}
                    getRowClass={(r) => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            ) : (
                <div className="card"><p className="muted">No model runs recorded for this marking pass.</p></div>
            )}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
    );
}
